import { useEffect, useState } from "react";
import { Button } from "@/shared/ui/button";

type UpdateFn = (reload?: boolean) => Promise<void>;

let pendingUpdate: UpdateFn | null = null;
const listeners = new Set<(fn: UpdateFn | null) => void>();

export function notifyUpdateAvailable(fn: UpdateFn): void {
  pendingUpdate = fn;
  for (const listener of listeners) listener(fn);
}

function subscribe(listener: (fn: UpdateFn | null) => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

type Props = Readonly<{
  /** When true, the banner is suppressed (e.g. during an active training session). */
  suppressed?: boolean;
}>;

export function PwaUpdateBanner({ suppressed = false }: Props): React.JSX.Element | null {
  const [updater, setUpdater] = useState<UpdateFn | null>(pendingUpdate);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => subscribe(setUpdater), []);

  if (!updater || dismissed || suppressed) return null;

  return (
    <output className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-xs text-[var(--color-foreground)] shadow-lg backdrop-blur">
      <span>New version available</span>
      <Button
        type="button"
        size="sm"
        onClick={() => {
          void updater(true);
        }}
      >
        Reload
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={() => setDismissed(true)}>
        Later
      </Button>
    </output>
  );
}
